import React from "react";
import { Bar, HorizontalBar, Line } from "react-chartjs-2";
import { colors, useTheme } from "@material-ui/core";
import { MyChartData } from "./MyChartData";
import numeral from "numeral";

const fmtBr = (v: number) => numeral(v).format(",0.[00]");

export interface LineChartProps {
  dados: MyChartData[];
  label: string;
  compareLabel?: string;
  horizontal?: boolean;
  colors?: string | string[];
  datasetOptions?: {};
}

export const BarChart = ({
  dados,
  label,
  compareLabel,
  horizontal,
  colors = [
    "#FF6633",
    "#FFB399",
    "#FF33FF",
    "#FFFF99",
    "#00B3E6",
    "#E6B333",
    "#3366E6",
    "#999966",
    "#99FF99",
    "#B34D4D",
    "#80B300",
    "#809900",
    "#E6B3B3",
    "#6680B3",
    "#66991A",
    "#FF99E6",
    "#CCFF1A",
    "#FF1A66",
    "#E6331A",
    "#33FFCC",
    "#66994D",
    "#B366CC",
    "#4D8000",
    "#B33300",
    "#CC80CC",
    "#66664D",
    "#991AFF",
    "#E666FF",
    "#4DB3FF",
    "#1AB399",
    "#E666B3",
    "#33991A",
    "#CC9999",
    "#B3B31A",
    "#00E680",
    "#4D8066",
    "#809980",
    "#E6FF80",
    "#1AFF33",
    "#999933",
    "#FF3380",
    "#CCCC00",
    "#66E64D",
    "#4D80CC",
    "#9900B3",
    "#E64D66",
    "#4DB380",
    "#FF4D4D",
    "#99E6E6",
    "#6666FF",
  ],
  datasetOptions,
}: LineChartProps) => {
  const theme = useTheme();
  const data = {
    datasets: [
      {
        backgroundColor: colors,
        data: dados.map((x) => x.value),
        label: label,
        barPercentage: 1.0,
        categoryPercentage: 0.8,
        ...datasetOptions,
      },
    ],
    labels: dados.map((x) => x.name),
  };

  if (compareLabel) {
    data.datasets.push({
      backgroundColor: colors,
      data: dados.map((x) => x.compareValue ?? 0),
      label: compareLabel,
      barPercentage: 1.0,
      categoryPercentage: 0.8,
      ...datasetOptions,
    });
  }

  const options = {
    animation: false,
    cornerRadius: 20,
    layout: { padding: 0 },
    legend: { display: false },
    maintainAspectRatio: true,
    responsive: true,
    scales: {
      xAxes: [
        {
          ticks: {
            fontColor: theme.palette.text.secondary,
            beginAtZero: true,
            min: 0,
            callback: (value: any) =>
              typeof value === "number" ? fmtBr(value) : value,
          },
          gridLines: {
            display: false,
            drawBorder: false,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            fontColor: theme.palette.text.secondary,
            beginAtZero: true,
            min: 0,
            callback: (value: any) =>
              typeof value === "number" ? fmtBr(value) : value,
          },
          gridLines: {
            borderDash: [2],
            borderDashOffset: [2],
            color: theme.palette.divider,
            drawBorder: false,
            zeroLineBorderDash: [2],
            zeroLineBorderDashOffset: [2],
            zeroLineColor: theme.palette.divider,
          },
        },
      ],
    },
    tooltips: {
      backgroundColor: theme.palette.background.default,
      bodyFontColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      enabled: true,
      footerFontColor: theme.palette.text.secondary,
      intersect: false,
      mode: "index",
      titleFontColor: theme.palette.text.primary,
      callbacks: {
        label: (item: any, data: any) => {
          const value = data.datasets[item.datasetIndex].data[
            item.index
          ] as number;
          return `${data.datasets[item.datasetIndex].label}: ${fmtBr(value)}`;
        },
      },
    },
  };

  if (horizontal === true)
    return <HorizontalBar data={data} options={options} />;
  else return <Bar data={data} options={options} />;
};

export const LineChart = ({ dados, label, compareLabel }: LineChartProps) => {
  const propsDefault = {
    fill: false,
    borderCapStyle: "butt",
    borderJoinStyle: "miter",
  };

  const theme = useTheme();
  const data = {
    datasets: [
      {
        backgroundColor: colors.blue[700].toString(),
        borderColor: colors.blue[700].toString(),
        data: dados.map((x) => x.value),
        label: label,
        ...propsDefault,
      },
    ],
    labels: dados.map((x) => x.name),
  };

  if (compareLabel) {
    data.datasets.push({
      backgroundColor: colors.blue[50].toString(),
      borderColor: colors.blue[50].toString(),
      data: dados.map((x) => x.compareValue ?? 0),
      label: compareLabel,
      ...propsDefault,
    });
  }

  const options = {
    //  animation: false,
    cornerRadius: 20,
    layout: { padding: 0 },
    legend: { display: false },
    maintainAspectRatio: true,
    responsive: true,
    tooltips: {
      backgroundColor: theme.palette.background.default,
      bodyFontColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      enabled: true,
      footerFontColor: theme.palette.text.secondary,
      intersect: false,
      mode: "index",
      titleFontColor: theme.palette.text.primary,
      callbacks: {
        label: (item: any, data: any) => {
          const value = data.datasets[item.datasetIndex].data[
            item.index
          ] as number;
          return `${data.datasets[item.datasetIndex].label}: ${fmtBr(value)}`;
        },
      },
    },
  };
  return <Line data={data} options={options} />;
};
